import { countries } from "@redotech/locale/countries";
import InfoIcon from "@redotech/redo-icons/info-circle_filled.svg";
import {
  InputProvider,
  groupInput,
  input,
  nonEmptyValidator,
} from "@redotech/ui/form";
import { memo } from "react";
import { RedoIcon } from "../../../../arbiter-components/icon/redo-icon";
import { RedoTextInput } from "../../../../arbiter-components/input/redo-text-input";
import { RedoExternalLink } from "../../../../arbiter-components/links/redo-external-link";
import { RedoSingleSelectDropdownInput } from "../../../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "../../../../flex";
import { Text } from "../../../../text";

export const RoyalMailV3CarrierInput = groupInput({
  accountNumber: input<string>({ validator: nonEmptyValidator }),
  obaEmailAddress: input<string>({ validator: nonEmptyValidator }),
  postingLocationNumber: input<string>({ validator: nonEmptyValidator }),
  obaAccessCode: input<string>(),
  registeredBillingPostcode: input<string>(),
  contactName: input<string>({ validator: nonEmptyValidator }),
  contactPhone: input<string>({ validator: nonEmptyValidator }),
  dispatchStreet1: input<string>({ validator: nonEmptyValidator }),
  dispatchStreet2: input<string>(),
  dispatchCity: input<string>({ validator: nonEmptyValidator }),
  dispatchZip: input<string>({ validator: nonEmptyValidator }),
  dispatchState: input<string>({ validator: nonEmptyValidator }),
  dispatchCountry: input<string>({ validator: nonEmptyValidator }),
});

export type RoyalMailV3CarrierValue = InputProvider.Value<
  typeof RoyalMailV3CarrierInput
>;

export type RoyalMailV3CarrierForm = InputProvider.Form<
  typeof RoyalMailV3CarrierInput
>;

export const royalMailV3CarrierDefault = (): RoyalMailV3CarrierValue => ({
  accountNumber: "",
  obaEmailAddress: "",
  postingLocationNumber: "",
  obaAccessCode: "",
  registeredBillingPostcode: "",
  contactName: "",
  contactPhone: "",
  dispatchStreet1: "",
  dispatchStreet2: "",
  dispatchCity: "",
  dispatchZip: "",
  dispatchState: "",
  dispatchCountry: "",
});

export const CreateRoyalMailV3CarrierForm = memo(
  function CreateRoyalMailV3CarrierForm({
    carrierInput,
  }: {
    carrierInput: RoyalMailV3CarrierForm;
  }) {
    return (
      <>
        <Flex align="center" gap="sm" mb="lg">
          <RedoIcon color="tertiary" Icon={InfoIcon} size={16} />
          <Flex align="flex-start" flexDirection="column" gap="xs">
            <Text color="tertiary" fontSize="xs">
              After creating this account, manual onboarding is required.
            </Text>
            <RedoExternalLink
              text="EasyPost v3 onboarding guide"
              url="https://docs.easypost.com/carriers/royal-mail-v3-guide"
            />
          </Flex>
        </Flex>
        <RedoTextInput
          label="Account Number"
          placeholder="If unknown, contact a Royal Mail v3 representative"
          setValue={carrierInput.inputs.accountNumber.setValue}
          value={carrierInput.inputs.accountNumber.value}
        />
        <RedoTextInput
          label="OBA Email Address"
          placeholder="Email registered with the Royal Mail account"
          setValue={carrierInput.inputs.obaEmailAddress.setValue}
          value={carrierInput.inputs.obaEmailAddress.value}
        />
        <RedoTextInput
          label="Posting Location Number"
          placeholder="10-digit number starting with 90, available from Royal Mail"
          setValue={carrierInput.inputs.postingLocationNumber.setValue}
          value={carrierInput.inputs.postingLocationNumber.value}
        />
        <RedoTextInput
          label="OBA Access Code"
          placeholder="Leave this blank - will be automatically added once approved"
          setValue={carrierInput.inputs.obaAccessCode.setValue}
          state="disabled"
          value={carrierInput.inputs.obaAccessCode.value}
        />
        <RedoTextInput
          label="Registered Billing Postcode"
          placeholder="Postcode registered with Royal Mail for invoicing (optional)"
          setValue={carrierInput.inputs.registeredBillingPostcode.setValue}
          value={carrierInput.inputs.registeredBillingPostcode.value}
        />
        <RedoTextInput
          label="Contact Name"
          placeholder=""
          setValue={carrierInput.inputs.contactName.setValue}
          value={carrierInput.inputs.contactName.value}
        />
        <RedoTextInput
          label="Contact Phone"
          placeholder=""
          setValue={carrierInput.inputs.contactPhone.setValue}
          value={carrierInput.inputs.contactPhone.value}
        />
        <RedoTextInput
          label="Dispatch Street 1"
          placeholder="Primary address from which shipments will be sent"
          setValue={carrierInput.inputs.dispatchStreet1.setValue}
          value={carrierInput.inputs.dispatchStreet1.value}
        />
        <RedoTextInput
          label="Dispatch Street 2"
          placeholder="Additional address from which shipments will be sent (optional)"
          setValue={carrierInput.inputs.dispatchStreet2.setValue}
          value={carrierInput.inputs.dispatchStreet2.value}
        />
        <RedoTextInput
          label="Dispatch City"
          placeholder="City from which shipments will be sent"
          setValue={carrierInput.inputs.dispatchCity.setValue}
          value={carrierInput.inputs.dispatchCity.value}
        />
        <RedoTextInput
          label="Dispatch ZIP"
          placeholder="ZIP code of the shipment origin"
          setValue={carrierInput.inputs.dispatchZip.setValue}
          value={carrierInput.inputs.dispatchZip.value}
        />
        <RedoTextInput
          label="Dispatch State"
          placeholder="State from which shipments will be sent"
          setValue={carrierInput.inputs.dispatchState.setValue}
          value={carrierInput.inputs.dispatchState.value}
        />
        <RedoSingleSelectDropdownInput
          label="Dispatch Country"
          options={countries.map((country) => ({
            id: country.code,
            type: "text",
            text: country.name,
            value: country.code,
          }))}
          optionSelected={({ item }) => {
            carrierInput.inputs.dispatchCountry.setValue(item.value);
          }}
          placeholder="Select country from which shipments will be sent"
          selectedItem={
            carrierInput.inputs.dispatchCountry.value
              ? {
                  id: carrierInput.inputs.dispatchCountry.value,
                  type: "text",
                  text:
                    countries.find(
                      (c) =>
                        c.code === carrierInput.inputs.dispatchCountry.value,
                    )?.name || carrierInput.inputs.dispatchCountry.value,
                  value: carrierInput.inputs.dispatchCountry.value,
                }
              : undefined
          }
        />
      </>
    );
  },
);
